// imports залишаються без змін
import React, { useEffect, useState } from "react";
import {
  Box, Button, TextField, Typography, IconButton, Paper, Checkbox,
  Table, TableHead, TableRow, TableCell, TableBody, TablePagination,
  Dialog, DialogTitle, DialogContent, DialogActions, Grid, Alert
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import PrintIcon from "@mui/icons-material/Print";
import {
  getPackages,
  createPackage,
  deletePackage,
} from "../../api/packages";
import TripSelect from "../../components/TripSelect";
import { searchCustomers, updateCustomer } from "../../api/customers";
import NewPackageForm from "../../components/NewPackageForm";
import { MuiTelInput } from "mui-tel-input";
import { useTranslation } from "react-i18next";
import { isPlatformAdminUser, isTenantAdminUser } from "../../utils/auth";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 200];

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const packagePrintHtml = ({ title, tripLabel, tripCaption, columns, rows, totals, totalLabel }) => `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)}</title>
  <style>
    @page { size: A4 portrait; margin: 12mm; }
    body { font-family: Arial, sans-serif; font-size: 11px; color: #111; }
    h1 { font-size: 16px; margin: 0 0 4px; }
    p { margin: 0 0 12px; }
    table { width: 100%; border-collapse: collapse; }
    th, td { border: 1px solid #222; padding: 4px 6px; vertical-align: top; text-align: left; word-break: break-word; }
    th { background: #f2f2f2; }
    .num { text-align: right; white-space: nowrap; }
    tfoot td { font-weight: 700; }
  </style>
</head>
<body>
  <h1>${escapeHtml(title)}</h1>
  ${tripLabel ? `<p>${escapeHtml(tripCaption)}: ${escapeHtml(tripLabel)}</p>` : ""}
  <table>
    <thead>
      <tr>${columns.map((column) => `<th>${escapeHtml(column)}</th>`).join("")}</tr>
    </thead>
    <tbody>
      ${rows
        .map(
          (cells) =>
            `<tr>${cells
              .map((cell, index) => `<td${index < 2 || (index >= 5 && index <= 7) ? ' class="num"' : ""}>${cell}</td>`)
              .join("")}</tr>`
        )
        .join("")}
    </tbody>
    <tfoot>
      <tr>
        <td colspan="5">${escapeHtml(totalLabel)}</td>
        <td class="num">${escapeHtml(totals.quantity)}</td>
        <td class="num">${escapeHtml(totals.weight)}</td>
        <td class="num">${escapeHtml(totals.price)}</td>
        <td></td>
      </tr>
    </tfoot>
  </table>
</body>
</html>`;

const CustomerPackagesPage = () => {
  const { t } = useTranslation("common");
  const canPrintPackages = isTenantAdminUser() || isPlatformAdminUser();
  const [trip, setTrip] = useState(null);
  const [packages, setPackages] = useState([]);
  const [newPackage, setNewPackage] = useState({
    quantity: null,
    weight_kg: "",
    price: "",
    internal_number: "",
    comment: "",
    customer: {
      first_name: "",
      last_name: "",
      phone_number: "",
      customer_id: null,
    },
    address: {
      city: "",
      street: "",
      is_nova_poshta: false,
      warehouse: "",
      address_id: null,
    },
  });
  const [originalCustomer, setOriginalCustomer] = useState(null);
  const [originalAddress, setOriginalAddress] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(PAGE_SIZE_OPTIONS[0]);
  const [totalCount, setTotalCount] = useState(0);
  const [selected, setSelected] = useState({});

  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerSearchInput, setCustomerSearchInput] = useState("");
  const [phoneSuggestions, setPhoneSuggestions] = useState([]);
  const [showPhoneDropdown, setShowPhoneDropdown] = useState(false);
  const [editCustomerDialogOpen, setEditCustomerDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [isUpdatingCustomer, setIsUpdatingCustomer] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // Допоміжна функція капіталізації імені/прізвища: перша літера кожного слова — велика
  const capitalizeName = (val) => {
    if (!val) return "";
    return String(val)
      .toLowerCase()
      .replace(/(^|[\s-])([\p{L}])/gu, (m, sep, ch) => `${sep}${ch.toUpperCase()}`);
  };

  useEffect(() => {
    const delay = setTimeout(async () => {
      const cleanPhone = newPackage.customer.phone_number;
      if (cleanPhone.length >= 13) {
        try {
          const res = await searchCustomers(cleanPhone);
          setPhoneSuggestions(res.data || []);
          setShowPhoneDropdown(true);
        } catch (e) {
          console.error("Phone autocomplete failed", e);
          setPhoneSuggestions([]);
        }
      } else {
        setPhoneSuggestions([]);
        setShowPhoneDropdown(false);
      }
    }, 400);
    return () => clearTimeout(delay);
  }, [newPackage.customer.phone_number]);

  const handlePhoneSuggestionSelect = (suggestion) => {
    const customer = {
      first_name: capitalizeName(suggestion.first_name),
      last_name: capitalizeName(suggestion.last_name),
      phone_number: suggestion.phone_number,
      customer_id: suggestion.customer_id,
    };
    const address = {
      ...suggestion.last_address,
      address_id: suggestion.last_address?.address_id || null,
    };
    setOriginalCustomer(customer);
    setOriginalAddress(address);
    setNewPackage((prev) => ({
      ...prev,
      customer,
      address,
    }));

    setCustomerSearchInput(capitalizeName(suggestion.last_name));
    setShowPhoneDropdown(false);
  };

  useEffect(() => {
    if (!trip) return;
    const fetch = async () => {
      const res = await getPackages(page + 1, rowsPerPage, trip.id);
      setPackages(res.data.results);
      setTotalCount(res.data.count);
    };
    fetch();
  }, [trip, page, rowsPerPage]);

  useEffect(() => {
    const delay = setTimeout(async () => {
      if (customerSearchInput.length >= 2) {
        try {
          const res = await searchCustomers(customerSearchInput);
          setCustomerOptions(res.data);
        } catch (e) {
          console.error("Customer search failed", e);
        }
      } else {
        setCustomerOptions([]);
      }
    }, 400);
    return () => clearTimeout(delay);
  }, [customerSearchInput]);

  const hasCustomerChanged = () =>
    !originalCustomer ||
    originalCustomer.first_name !== newPackage.customer.first_name ||
    originalCustomer.last_name !== newPackage.customer.last_name ||
    originalCustomer.phone_number !== newPackage.customer.phone_number;

  const hasAddressChanged = () =>
    !originalAddress ||
    originalAddress.city !== newPackage.address.city ||
    originalAddress.street !== newPackage.address.street ||
    originalAddress.is_nova_poshta !== newPackage.address.is_nova_poshta ||
    originalAddress.warehouse !== newPackage.address.warehouse;

  /** Зводить помилки з бекенда (вкладені об'єкти) до плоского { "customer.first_name": "текст" }. */
  const flattenApiErrors = (apiErrors) => {
    if (!apiErrors || typeof apiErrors !== "object") return {};
    const flat = {};
    const walk = (obj, prefix = "") => {
      Object.keys(obj).forEach((key) => {
        const val = obj[key];
        const path = prefix ? `${prefix}.${key}` : key;
        if (val && typeof val === "object" && !Array.isArray(val)) {
          walk(val, path);
        } else if (Array.isArray(val) && val.length) {
          flat[path] = val[0];
        } else if (typeof val === "string") {
          flat[path] = val;
        }
      });
    };
    walk(apiErrors);
    return flat;
  };

  /** Валідація форми перед відправкою (фронтенд). Повертає об'єкт помилок або null. */
  const validateForm = () => {
    const e = {};
    const firstName = (newPackage.customer.first_name || "").trim();
    const lastName = (newPackage.customer.last_name || "").trim();
    const phone = (newPackage.customer.phone_number || "").trim();
    const city = (newPackage.address.city || "").trim();
    const street = (newPackage.address.street || "").trim();
    const warehouse = (newPackage.address.warehouse || "").trim();
    const isNovaPoshta = newPackage.address.is_nova_poshta;

    if (!firstName) e["customer.first_name"] = t("packagesAdmin.required");
    if (!lastName) e["customer.last_name"] = t("packagesAdmin.required");
    if (!phone) e["customer.phone_number"] = t("packagesAdmin.required");
    else if (phone.replace(/\D/g, "").length < 10) e["customer.phone_number"] = t("packagesAdmin.phoneInvalid");
    if (!city) e["address.city"] = t("packagesAdmin.required");
    if (isNovaPoshta) {
      if (!warehouse) e["address.warehouse"] = t("packagesAdmin.warehouseRequired");
    } else {
      if (!street) e["address.street"] = t("packagesAdmin.streetRequired");
    }
    const qty = newPackage.quantity != null && newPackage.quantity !== "" ? Number(newPackage.quantity) : NaN;
    if (Number.isNaN(qty) || qty < 1) e.quantity = t("packagesAdmin.minQty");
    const weight = newPackage.weight_kg != null && newPackage.weight_kg !== "" ? Number(newPackage.weight_kg) : NaN;
    if (Number.isNaN(weight) || weight < 0) e.weight_kg = t("packagesAdmin.requiredNonNegative");
    const priceVal = newPackage.price != null && newPackage.price !== "" ? Number(newPackage.price) : NaN;
    if (Number.isNaN(priceVal) || priceVal < 0) e.price = t("packagesAdmin.requiredNonNegative");
    return Object.keys(e).length ? e : null;
  };

  const handleAdd = async () => {
    setFormErrors({});
    const frontErrors = validateForm();
    if (frontErrors) {
      setFormErrors(frontErrors);
      return;
    }

    const useExistingCustomer = !hasCustomerChanged() && newPackage.customer.customer_id;
    const useExistingAddress = !hasAddressChanged() && newPackage.address.address_id;

    const payload = {
      trip: trip.id,
      quantity: Number(newPackage.quantity) || 1,
      weight_kg: Number(newPackage.weight_kg),
      price: Number(newPackage.price),
      internal_number: newPackage.internal_number != null && newPackage.internal_number !== "" ? Number(newPackage.internal_number) : null,
      comment: newPackage.comment || "",
    };
    if (useExistingCustomer) {
      payload.customer_id = newPackage.customer.customer_id;
    } else {
      payload.customer = {
        first_name: capitalizeName(newPackage.customer.first_name),
        last_name: capitalizeName(newPackage.customer.last_name),
        phone_number: newPackage.customer.phone_number,
      };
    }
    if (useExistingAddress) {
      payload.address_id = newPackage.address.address_id;
    } else {
      payload.address = {
        city: newPackage.address.city,
        street: newPackage.address.street || "",
        is_nova_poshta: newPackage.address.is_nova_poshta,
        warehouse: newPackage.address.warehouse || "",
      };
    }

    try {
      await createPackage(payload);
      setNewPackage({
        quantity: "",
        weight_kg: "",
        price: "",
        internal_number: "",
        comment: "",
        customer: { first_name: "", last_name: "", phone_number: "", customer_id: "" },
        address: { city: "", street: "", is_nova_poshta: false, warehouse: "", address_id: "" }
      });
      setOriginalCustomer(null);
      setOriginalAddress(null);
      setCustomerSearchInput("");
      setFormErrors({});
      const res = await getPackages(page + 1, rowsPerPage, trip.id);
      setPackages(res.data.results);
      setCustomerOptions([]);
    } catch (err) {
      const status = err.response?.status;
      const apiErrors = err.response?.data;
      // При 400 показуємо польові помилки з бекенду по відповідних полях
      if (status === 400 && apiErrors && typeof apiErrors === "object") {
        const flat = flattenApiErrors(apiErrors);
        // Помилки без відповідного поля у формі (trip, customer_id, address_id) показуємо в Alert
        const noFieldKeys = ["trip", "customer_id", "address_id"];
        const generalMsg = noFieldKeys
          .filter((k) => flat[k])
          .map((k) => flat[k])
          .join(". ");
        if (generalMsg) {
          flat._form = flat._form ? `${flat._form}. ${generalMsg}` : generalMsg;
          noFieldKeys.forEach((k) => delete flat[k]);
        }
        setFormErrors(flat);
      } else {
        const detail = apiErrors?.detail;
        const message = typeof detail === "string" ? detail : Array.isArray(detail) ? detail[0] : detail;
        setFormErrors({
          _form: message || t("packagesAdmin.addError"),
        });
      }
    }
  };

  const handleDelete = async (id) => {
    try {
      await deletePackage(id);
      setSelected((prev) => {
        if (!prev[id]) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
      const res = await getPackages(page + 1, rowsPerPage, trip.id);
      setPackages(res.data.results);
    } catch (err) {
      console.error("Delete error", err);
    }
  };

  const selectedCount = Object.keys(selected).length;
  const pageIds = packages.map((pkg) => pkg.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selected[id]);
  const somePageSelected = pageIds.some((id) => selected[id]);

  const togglePackage = (pkg) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[pkg.id]) delete next[pkg.id];
      else next[pkg.id] = pkg;
      return next;
    });
  };

  const togglePage = () => {
    setSelected((prev) => {
      const next = { ...prev };
      if (allPageSelected) pageIds.forEach((id) => delete next[id]);
      else packages.forEach((pkg) => {
        next[pkg.id] = pkg;
      });
      return next;
    });
  };

  const addressLines = (pkg) => {
    const place = pkg.address?.is_nova_poshta
      ? t("packagesAdmin.novaPoshtaBranch", { warehouse: pkg.address.warehouse })
      : t("packagesAdmin.addressDelivery");
    return `${pkg.address?.city || ""}, ${pkg.address?.street || ""}\n${place}`;
  };

  const printSelected = () => {
    const list = Object.values(selected).sort(
      (a, b) => (Number(a.number) || 0) - (Number(b.number) || 0)
    );
    if (!list.length) return;
    const rows = list.map((pkg) => [
      escapeHtml(pkg.number),
      escapeHtml(pkg.internal_number),
      escapeHtml(addressLines(pkg)).replace(/\n/g, "<br>"),
      escapeHtml(`${capitalizeName(pkg.customer?.first_name)} ${capitalizeName(pkg.customer?.last_name)}`.trim()),
      escapeHtml(pkg.customer?.phone_number),
      escapeHtml(pkg.quantity),
      escapeHtml(pkg.weight_kg),
      escapeHtml(`${pkg.price} €`),
      escapeHtml(pkg.comment),
    ]);
    const html = packagePrintHtml({
      title: t("packagesAdmin.printTitle"),
      tripLabel: trip?.label || trip?.route?.name || "",
      tripCaption: t("packagesAdmin.printTrip"),
      columns: [
        "№",
        t("packagesAdmin.internalNumber"),
        t("packagesAdmin.address"),
        t("packagesAdmin.recipient"),
        t("packagesAdmin.phone"),
        t("packagesAdmin.qty"),
        t("packagesAdmin.weight"),
        t("packagesAdmin.price"),
        t("packagesAdmin.comment"),
      ],
      rows,
      totals: {
        quantity: list.reduce((sum, pkg) => sum + (Number(pkg.quantity) || 0), 0),
        weight: list.reduce((sum, pkg) => sum + (Number(pkg.weight_kg) || 0), 0).toFixed(2),
        price: `${list.reduce((sum, pkg) => sum + (Number(pkg.price) || 0), 0).toFixed(2)} €`,
      },
      totalLabel: t("packagesAdmin.printTotal"),
    });
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      alert(t("packagesAdmin.printBlocked"));
      return;
    }
    let printed = false;
    const runPrint = () => {
      if (printed) return;
      printed = true;
      printWindow.focus();
      printWindow.print();
    };
    printWindow.onload = runPrint;
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    if (printWindow.document.readyState === "complete") runPrint();
  };

  const handleEditCustomer = () => {
    setEditingCustomer({
      first_name: newPackage.customer.first_name,
      last_name: newPackage.customer.last_name,
      phone_number: newPackage.customer.phone_number,
    });
    setEditCustomerDialogOpen(true);
  };

  const handleSaveCustomerEdit = async () => {
    if (!newPackage.customer.customer_id) {
      // Якщо немає customer_id, просто закриваємо діалог
      setEditCustomerDialogOpen(false);
      setEditingCustomer(null);
      return;
    }

    setIsUpdatingCustomer(true);
    try {
      const customerData = {
        first_name: capitalizeName(editingCustomer.first_name),
        last_name: capitalizeName(editingCustomer.last_name),
        phone_number: editingCustomer.phone_number,
      };

      // Відправляємо запит на оновлення клієнта на бекенді
      await updateCustomer(newPackage.customer.customer_id, customerData);

      // Після успішного оновлення оновлюємо дані клієнта, залишаючи customer_id
      const updatedCustomer = {
        ...newPackage.customer,
        first_name: customerData.first_name,
        last_name: customerData.last_name,
        phone_number: customerData.phone_number,
        customer_id: newPackage.customer.customer_id, // Залишаємо customer_id, щоб поля залишилися заблокованими
      };

      setNewPackage((prev) => ({
        ...prev,
        customer: updatedCustomer,
      }));

      // Оновлюємо originalCustomer з новими даними
      setOriginalCustomer(updatedCustomer);

      setEditCustomerDialogOpen(false);
      setEditingCustomer(null);
    } catch (error) {
      console.error("Помилка оновлення клієнта:", error);
      alert(t("packagesAdmin.updateCustomerError"));
    } finally {
      setIsUpdatingCustomer(false);
    }
  };

  const handleCancelCustomerEdit = () => {
    setEditCustomerDialogOpen(false);
    setEditingCustomer(null);
  };

  const handleClearCustomer = () => {
    setNewPackage((prev) => ({
      ...prev,
      customer: {
        first_name: "",
        last_name: "",
        phone_number: "",
        customer_id: null,
      },
      address: {
        city: "",
        street: "",
        is_nova_poshta: false,
        warehouse: "",
        address_id: null,
      },
    }));
    setOriginalCustomer(null);
    setOriginalAddress(null);
    setCustomerSearchInput("");
  };

  return (
    <Box p={2} sx={{ mt: 2 }}>
      <Typography variant="h5" gutterBottom>{t("packagesAdmin.pickTrip")}</Typography>
      <TripSelect
        value={trip}
        onChange={(next) => {
          if (String(trip?.id || "") !== String(next?.id || "")) {
            setPage(0);
            setSelected({});
          }
          setTrip(next);
        }}
        dateFrom={new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)}
      />

      {trip && (
        <Box sx={{ mt: 2, mb: 2 }}>
          {formErrors._form && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFormErrors((p) => ({ ...p, _form: undefined }))}>
              {formErrors._form}
            </Alert>
          )}
          <NewPackageForm
            newPackage={newPackage}
            setNewPackage={setNewPackage}
            customerOptions={customerOptions}
            customerSearchInput={customerSearchInput}
            setCustomerSearchInput={setCustomerSearchInput}
            showPhoneDropdown={showPhoneDropdown}
            setShowPhoneDropdown={setShowPhoneDropdown}
            phoneSuggestions={phoneSuggestions}
            handlePhoneSuggestionSelect={handlePhoneSuggestionSelect}
            originalCustomer={originalCustomer}
            setOriginalCustomer={setOriginalCustomer}
            originalAddress={originalAddress}
            setOriginalAddress={setOriginalAddress}
            handleAdd={handleAdd}
            onEditCustomer={handleEditCustomer}
            onClearCustomer={handleClearCustomer}
            errors={formErrors}
            onClearFieldError={(key) => setFormErrors((p) => {
              const next = { ...p };
              delete next[key];
              if (next._form) delete next._form;
              return next;
            })}
          />
        </Box>
      )}

      {/* Модальне вікно для редагування клієнта */}
      <Dialog
        open={editCustomerDialogOpen}
        onClose={handleCancelCustomerEdit}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("packagesAdmin.editCustomer")}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <MuiTelInput
                defaultCountry="UA"
                fullWidth
                size="small"
                label={t("packagesAdmin.phone")}
                value={editingCustomer?.phone_number || ""}
                onChange={(value) =>
                  setEditingCustomer((prev) => ({
                    ...prev,
                    phone_number: value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label={t("packagesAdmin.firstName")}
                value={editingCustomer?.first_name || ""}
                onChange={(e) =>
                  setEditingCustomer((prev) => ({
                    ...prev,
                    first_name: capitalizeName(e.target.value),
                  }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label={t("packagesAdmin.lastName")}
                value={editingCustomer?.last_name || ""}
                onChange={(e) =>
                  setEditingCustomer((prev) => ({
                    ...prev,
                    last_name: capitalizeName(e.target.value),
                  }))
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelCustomerEdit} disabled={isUpdatingCustomer}>
            {t("packagesAdmin.cancel")}
          </Button>
          <Button 
            onClick={handleSaveCustomerEdit} 
            variant="contained"
            disabled={isUpdatingCustomer}
          >
            {isUpdatingCustomer ? t("packagesAdmin.saving") : t("packagesAdmin.save")}
          </Button>
        </DialogActions>
      </Dialog>

      {!trip ? (
        <Typography variant="body1" mt={2}>⚠️ {t("packagesAdmin.pickTripFirst")}</Typography>
      ) : (
        <>
        {canPrintPackages ? (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              disabled={selectedCount === 0}
              onClick={printSelected}
            >
              {t("packagesAdmin.printList")}
              {selectedCount > 0 ? ` · ${selectedCount}` : ""}
            </Button>
          </Box>
        ) : null}
        <Paper sx={{ mt: canPrintPackages ? 1 : 3, overflowX: "auto" }}>
          <Table sx={{ minWidth: 850 }}>
            <TableHead>
              <TableRow>
                {canPrintPackages ? (
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={allPageSelected}
                      indeterminate={!allPageSelected && somePageSelected}
                      onChange={togglePage}
                      disabled={!packages.length}
                      inputProps={{ "aria-label": t("packagesAdmin.selectAll") }}
                    />
                  </TableCell>
                ) : null}
                <TableCell sx={{ width: 40 }}>№</TableCell>
                <TableCell sx={{ width: 40 }}>{t("packagesAdmin.internalNumber")}</TableCell>
                <TableCell sx={{ width: 320, minWidth: 150 }}>{t("packagesAdmin.address")}</TableCell>
                <TableCell sx={{ width: 320, minWidth: 150 }}>{t("packagesAdmin.recipient")}</TableCell>
                <TableCell sx={{ width: 320, minWidth: 180 }}>{t("packagesAdmin.phone")}</TableCell>
                <TableCell sx={{ minWidth: 100 }}>{t("packagesAdmin.qty")}</TableCell>
                <TableCell sx={{ minWidth: 100 }}>{t("packagesAdmin.weight")}</TableCell>
                <TableCell sx={{ minWidth: 100 }}>{t("packagesAdmin.price")}</TableCell>
                <TableCell sx={{ minWidth: 100 }}>{t("packagesAdmin.comment")}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {packages.map((pkg) => (
                <TableRow key={pkg.id} selected={canPrintPackages && Boolean(selected[pkg.id])}>
                  {canPrintPackages ? (
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={Boolean(selected[pkg.id])}
                        onChange={() => togglePackage(pkg)}
                        inputProps={{ "aria-label": t("packagesAdmin.selectPackage", { number: pkg.number }) }}
                      />
                    </TableCell>
                  ) : null}
                  <TableCell>{pkg.number}</TableCell>
                  <TableCell>{pkg.internal_number}</TableCell>
                  <TableCell>
                    {pkg.address.city}, {pkg.address.street}<br />
                    {pkg.address.is_nova_poshta
                      ? t("packagesAdmin.novaPoshtaBranch", { warehouse: pkg.address.warehouse })
                      : t("packagesAdmin.addressDelivery")}
                  </TableCell>
                  <TableCell>{capitalizeName(pkg.customer.first_name)} {capitalizeName(pkg.customer.last_name)}</TableCell>
                  <TableCell>{pkg.customer.phone_number}</TableCell>
                  <TableCell>{pkg.quantity}</TableCell>
                  <TableCell>{pkg.weight_kg}</TableCell>
                  <TableCell>{pkg.price} €</TableCell>
                  <TableCell>{pkg.comment}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleDelete(pkg.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={canPrintPackages ? 6 : 5} align="right" sx={{ fontWeight: 600 }}>
                  {t("packagesAdmin.pageTotal")}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {packages.reduce((sum, p) => sum + (Number(p.quantity) || 0), 0)}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {packages.reduce((sum, p) => sum + (Number(p.weight_kg) || 0), 0).toFixed(2)}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {packages.reduce((sum, p) => sum + (Number(p.price) || 0), 0).toFixed(2)} €
                </TableCell>
                <TableCell colSpan={2} />
              </TableRow>
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            labelRowsPerPage={t("packagesAdmin.rowsPerPage")}
          />
        </Paper>
        </>
      )}
    </Box>
  );
};

export default CustomerPackagesPage;